import { Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ReportExportViewService } from '../../report-export-view.service';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ViewTemplate, ViewTemplateCatalog } from '../../models/template.model';
import { ActivatedRoute } from '@angular/router';
import { SelectedTemplateService } from './selected-template.service';
import { INFO_USER_RESOLVER_TOKEN, UserInfoResolver } from '../../report-export-view.token';

export interface SelectTemplateDialogInputData {
  reportId: number;
  viewId: number;
  selectedTemplateId?: number;
  templates: ViewTemplateCatalog;
}

const PRIORITY_TEMPLATE = {
  Office: 'US Office',
  Industrial: 'US Industrial',
  Retail: 'US Retail',
};

const PERMISSION_APPROVED_STATUS = 'Approved';

@UntilDestroy()
@Component({
  selector: 'ppt-select-template-dialog',
  templateUrl: './select-template-dialog.component.html',
  styleUrls: ['./select-template-dialog.component.scss'],
  standalone: false,
})
export class SelectTemplateDialogComponent implements OnDestroy {
  public templatesCatalog: ViewTemplateCatalog;
  public templateFC: UntypedFormControl;
  public selectedTemplateImageSrc: string;

  private reportId: number;
  private viewId: number;
  private readonly currentlySelectedTempId: number;
  private defaultTemplateId: number;

  constructor(
    public dialogRef: MatDialogRef<SelectTemplateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: SelectTemplateDialogInputData,
    private reportExportViewService: ReportExportViewService,
    private formBuilder: UntypedFormBuilder,
    private activatedRoute: ActivatedRoute,
    private selectedViewTemplateService: SelectedTemplateService,
    @Inject(INFO_USER_RESOLVER_TOKEN) private userInfoResolver: UserInfoResolver
  ) {
    if (data != null) {
      this.defaultTemplateId = this.getDefaultTemplateId(data.templates);
      const selectedTemplateId = data.selectedTemplateId != null ? data.selectedTemplateId : this.defaultTemplateId;
      this.currentlySelectedTempId = data.selectedTemplateId;
      this.templateFC = this.formBuilder.control(selectedTemplateId, Validators.required);
      this.reportId = data.reportId;
      this.viewId = data.viewId;
      this.templatesCatalog = data.templates;
      this._setSelectedTemplateImageSrc(selectedTemplateId);
      this._setImageSrcChangeEvent();
    }
  }

  ngOnDestroy() {
    // required by angular-l10n
  }
  private getDefaultTemplateId(viewTemplateCatalog: ViewTemplateCatalog): number {
    const mappingTemplateNameIds = viewTemplateCatalog.reduce(
      (acc, template) => ({ ...acc, [template.templateName]: template.templateId }),
      {}
    );
    const userLocationPerms = this.userInfoResolver.getUserLocationPerms();
    const propertyUsageTypeVals = (userLocationPerms || [])
      .filter(perm => perm.accessRequestStatusVal === PERMISSION_APPROVED_STATUS)
      .map(perm => perm.propertyUsageTypeVal);
    for (let key in PRIORITY_TEMPLATE) {
      if (propertyUsageTypeVals.includes(key)) {
        return mappingTemplateNameIds[PRIORITY_TEMPLATE[key]];
      }
    }
    return mappingTemplateNameIds[PRIORITY_TEMPLATE['Office']];
  }

  public selectTemplate() {
    const selectedTemplate = this._getSelectedTemplateFromCatalog(this.templateFC.value);
    this.selectedViewTemplateService.setSelectedViewTemplate(selectedTemplate);
    this.reportExportViewService
      .saveTemplate(this.reportId, this.viewId, selectedTemplate)
      .subscribe(() => this.onClose(selectedTemplate.templateId));
  }

  public onClose(selectedTemplateId?: number): void {
    if (selectedTemplateId == null) {
      const currentTemplate = this._getSelectedTemplateFromCatalog(this.templateFC.value) || { templateId: null };
      selectedTemplateId = currentTemplate.templateId;
    }
    const templateIdToEmit = selectedTemplateId !== this.currentlySelectedTempId ? selectedTemplateId : null;
    this.dialogRef.close(templateIdToEmit);
  }

  private _setSelectedTemplateImageSrc(selectedTemplateId: number | null) {
    const selectedTemplate = this.templatesCatalog.find(template => template.templateId === selectedTemplateId);
    this.selectedTemplateImageSrc = selectedTemplate ? selectedTemplate.imageUrl : '';
  }

  private _setImageSrcChangeEvent() {
    this.templateFC.valueChanges.pipe(untilDestroyed(this)).subscribe((selectedTemplateId: number) => {
      this._setSelectedTemplateImageSrc(selectedTemplateId);
    });
  }
  private _getSelectedTemplateFromCatalog(selectedTemplateId: number): ViewTemplate {
    return this.templatesCatalog.find(template => template.templateId === selectedTemplateId);
  }
}
